import { useState } from "react";
import type { Member, MemberDraft } from "@/services/types";
import { memberService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { TextField } from "@/components/base/TextField";

interface CreateMemberModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (member: Member) => void;
}

/**
 * A compact "create new member" shortcut used from enrolment search. It saves
 * through the same centralized member service as the full add-member form, so a
 * freshly created member is immediately available for ticket assignment.
 */
export function CreateMemberModal({ open, onClose, onCreated }: CreateMemberModalProps) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setPhone("");
    setCity("");
    setError(null);
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) {
      setError("Full name is required.");
      return;
    }
    const normalized = phone.replace(/[^0-9]/g, "").replace(/^(91|0)/, "");
    if (!/^[6-9]\d{9}$/.test(normalized)) {
      setError("Enter a valid 10-digit mobile starting with 6, 7, 8 or 9.");
      return;
    }
    setSaving(true);
    try {
      const draft: MemberDraft = {
        fullName: name.trim(),
        guardianName: "",
        primaryPhone: normalized,
        alternatePhone: "",
        email: "",
        addressLine1: "",
        addressLine2: "",
        city: city.trim(),
        state: "",
        pinCode: "",
        proofType: "",
        proofNumber: "",
        bankAccountName: "",
        bankAccountNumber: "",
        ifsc: "",
        upiId: "",
        isOrganizer: false,
        status: "active",
        notes: "",
      };
      const created = await memberService.createMember(draft, "Staff User");
      toast.success("Member added", `${created.fullName} (${created.memberCode}) created and ready to assign.`);
      reset();
      onClose();
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save member.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create new member"
      description="Quick-add a member who is not yet in the system. They will be assigned straight away."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button icon={<i className="ri-user-add-line" />} loading={saving} onClick={save}>
            Create &amp; assign
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField label="Full name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh Gupta" />
        <TextField label="Mobile" required inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" />
        <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Optional" />
        {error ? <p className="text-xs text-danger-600">{error}</p> : null}
      </div>
    </Modal>
  );
}